function trimText(value) {
  return String(value ?? '').trim();
}

export const DEFAULT_AAR_UPLOAD_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

const DRIVE_MIME_EXTENSION_MAP = {
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

function toValidDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function truncateAtWordBoundary(value, maxLength) {
  if (value.length <= maxLength) return value;

  const truncated = value.slice(0, maxLength).trim();
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  if (lastSpaceIndex >= Math.floor(maxLength * 0.6)) {
    return truncated.slice(0, lastSpaceIndex).trim();
  }

  return truncated;
}

function stripControlCharacters(value) {
  return Array.from(value).map((char) => {
    const code = char.charCodeAt(0);
    return code <= 31 || code === 127 ? ' ' : char;
  }).join('');
}

// Supabase Storage object keys only accept a narrow ASCII character set, so these
// friendly names have to be reduced to that set deterministically. Curly quotes are
// dropped (Center’s -> Centers), accents are folded to ASCII (José -> Jose), and any
// remaining unsupported character becomes a space so words never run together.
const COMBINING_MARKS = /[\u0300-\u036f]/g;
const QUOTE_PUNCTUATION = /[\u00ab\u00b4\u00bb\u02b9-\u02bc\u2018-\u201f\u2032-\u2036\u2039\u203a]/g;
const DASH_PUNCTUATION = /[\u2010-\u2015\u2043\u2212]/g;
const UNSUPPORTED_CHARACTERS = /[^A-Za-z0-9 !'(),\-.;=@_+$]/g;

function normalizeToStorageSafeText(value) {
  return value
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '')
    .replace(QUOTE_PUNCTUATION, '')
    .replace(DASH_PUNCTUATION, '-')
    .replace(/&/g, ' and ')
    .replace(UNSUPPORTED_CHARACTERS, ' ');
}

export function sanitizeDriveName(value, { fallback = 'Untitled', maxLength = 80 } = {}) {
  const normalized = normalizeToStorageSafeText(
    stripControlCharacters(trimText(value)).replace(/[\\/:*?"<>|]+/g, ' '),
  )
    .replace(/\s+/g, ' ')
    .trim();

  const cleaned = normalized.replace(/^[.\-_\s]+|[.\-_\s]+$/g, '');
  const safeValue = cleaned || trimText(fallback) || 'Untitled';
  return truncateAtWordBoundary(safeValue, Math.max(Number(maxLength) || 0, 1));
}

export function getQuarterFolderName(value) {
  const date = toValidDate(value);
  if (!date) return '';

  const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
  return `Q${quarter} ${date.getUTCFullYear()}`;
}

export function getShortDriveId(value) {
  const normalized = trimText(value);
  if (!normalized) return 'unknown';

  const uuidPrefixMatch = normalized.match(/^([0-9a-f]{8})-[0-9a-f-]+$/i);
  if (uuidPrefixMatch) {
    return uuidPrefixMatch[1].toLowerCase();
  }

  const alphanumeric = normalized.replace(/[^a-z0-9]/gi, '');
  if (!alphanumeric) return 'unknown';
  if (alphanumeric.length <= 8) return alphanumeric.toLowerCase();
  return alphanumeric.slice(-8).toLowerCase();
}

function formatDriveDatePart(value) {
  const date = toValidDate(value);
  if (!date) return 'Undated';

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function buildEventFolderName({ eventDate, eventTitle, shortId } = {}) {
  const datePart = formatDriveDatePart(eventDate);
  const titlePart = sanitizeDriveName(eventTitle, {
    fallback: 'Untitled event',
    maxLength: 96,
  });
  const idPart = sanitizeDriveName(shortId, {
    fallback: 'unknown',
    maxLength: 24,
  });

  return `${datePart} - ${titlePart} - ${idPart}`;
}

export function buildSubmissionFolderName({ workflowType, submitterName } = {}) {
  const normalizedWorkflowType = trimText(workflowType).toLowerCase() === 'photo_only'
    ? 'photo_only'
    : 'full';
  const submitterPart = sanitizeDriveName(submitterName, {
    fallback: 'Unknown submitter',
    maxLength: 96,
  });

  return `${normalizedWorkflowType} - ${submitterPart}`;
}

export function getDriveFileExtensionFromMimeType(value) {
  const normalizedMimeType = trimText(value).toLowerCase();
  return DRIVE_MIME_EXTENSION_MAP[normalizedMimeType] || null;
}

export function buildDrivePhotoFilename({ eventTitle, submitterName, sequence, mimeType } = {}) {
  const extension = getDriveFileExtensionFromMimeType(mimeType);
  if (!extension) {
    throw new Error('Unsupported photo MIME type.');
  }

  const eventPart = sanitizeDriveName(eventTitle, {
    fallback: 'Untitled event',
    maxLength: 96,
  });
  const submitterPart = sanitizeDriveName(submitterName, {
    fallback: 'Unknown submitter',
    maxLength: 72,
  });
  const sequenceNumber = Number.isFinite(Number(sequence)) && Number(sequence) > 0
    ? Math.floor(Number(sequence))
    : 1;
  const sequencePart = String(sequenceNumber).padStart(2, '0');

  return `${eventPart} - ${submitterPart} - ${sequencePart}.${extension}`;
}
